import { useEffect, useMemo, useState } from "react";
import Modal from "../../../shared/components/Modal/Modal";
import images from "../../../utilities/images/images";
import "./CustomizeSubtopicModal.css";

const DIFFICULTY_OPTIONS = ["Easy", "Medium", "Hard"];

const normalizeDifficultyValue = (value = "Easy") => {
  const normalized = String(value).toLowerCase();
  if (normalized.includes("hard")) return "Hard";
  if (normalized.includes("medium")) return "Medium";
  return "Easy";
};

const CustomizeSubtopicModal = ({
  isOpen,
  onClose,
  onSave,
  topics = [],
  excludedTopicIds = [],
  initialTopicId = "",
  initialTopicName = "",
  initialDifficulty = "Hard",
}) => {
  const normalizedInitialTopicId = String(initialTopicId || "");
  const normalizedInitialTopicName = String(initialTopicName || "")
    .trim()
    .toLowerCase();
  const normalizedInitialDifficulty = normalizeDifficultyValue(initialDifficulty || "Easy");
  const normalizedTopics = useMemo(
    () =>
      (Array.isArray(topics) ? topics : []).map((topic, index) => {
        const topicId = String(
          topic?.sub_topic_id ||
            topic?.subtopic_id ||
            topic?.id ||
            topic?.topic_id ||
            topic?.sub_topic_name ||
            topic?.subtopic_name ||
            `subtopic-${index}`
        );
        const topicDifficulty = normalizeDifficultyValue(
          topic?.current_difficulty ||
            topic?.selecteddifficulty ||
            topic?.difficulty ||
            topic?.level
        );
        const isInitialTopic = topicId === normalizedInitialTopicId;

        return {
          id: topicId,
          name:
            topic?.sub_topic_name ||
            topic?.subtopic_name ||
            topic?.topic_name ||
            topic?.name ||
            "Subtopic",
          level:
            topic?.mastery_tag ||
            topic?.subtopic_level ||
            topic?.level ||
            "Medium Mastery",
          status:
            topic?.ui_status || topic?.subtopic_status || topic?.status || "Practicing",
          difficulty: isInitialTopic ? normalizedInitialDifficulty : topicDifficulty,
          isSelected: topic?.is_selected === 1 || topic?.is_selected === true,
        };
      }),
    [topics, normalizedInitialTopicId, normalizedInitialDifficulty]
  );
  const availableTopics = useMemo(() => {
    if (!Array.isArray(excludedTopicIds) || excludedTopicIds.length === 0) {
      return normalizedTopics;
    }
    const blockedIds = new Set(excludedTopicIds.map((id) => String(id)));
    return normalizedTopics.filter(
      (topicItem) =>
        !blockedIds.has(String(topicItem.id)) || String(topicItem.id) === String(initialTopicId)
    );
  }, [excludedTopicIds, initialTopicId, normalizedTopics]);

  const [selectedTopicId, setSelectedTopicId] = useState(initialTopicId);
  const [difficulty, setDifficulty] = useState(initialDifficulty);
  const [isTopicMenuOpen, setIsTopicMenuOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const selectedTopicFromApi =
      availableTopics.find((topic) => topic.isSelected)?.id || availableTopics[0]?.id || "";
    const topicMatchedById = availableTopics.find(
      (topic) => String(topic.id) === String(initialTopicId)
    );
    const topicMatchedByName = normalizedInitialTopicName
      ? availableTopics.find(
          (topic) => String(topic.name || "").trim().toLowerCase() === normalizedInitialTopicName
        )
      : null;
    const resolvedTopicId =
      topicMatchedById?.id || topicMatchedByName?.id || initialTopicId || selectedTopicFromApi;
    const resolvedTopic =
      availableTopics.find((topic) => String(topic.id) === String(resolvedTopicId)) ||
      availableTopics[0] ||
      null;

    setSelectedTopicId(resolvedTopic?.id || resolvedTopicId);
    setDifficulty(
      normalizeDifficultyValue(initialDifficulty || resolvedTopic?.difficulty || "Hard")
    );
    setIsTopicMenuOpen(false);
  }, [
    isOpen,
    initialTopicId,
    initialDifficulty,
    availableTopics,
    normalizedInitialTopicName,
  ]);

  const selectedTopic =
    availableTopics.find((item) => String(item.id) === String(selectedTopicId)) ||
    availableTopics[0] ||
    null;

  const handleSave = () => {
    if (!selectedTopic) return;
    onSave?.({
      topicId: selectedTopic.id,
      difficulty,
      topic: selectedTopic,
    });
  };

  const getStatusClassName = (status = "") => {
    const normalizedStatus = String(status).toLowerCase();
    if (normalizedStatus.includes("master")) return "mastered";
    if (normalizedStatus.includes("practic")) return "practicing";
    return "default";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="min(36rem, 88vw)"
      height="auto"
      overflowY="visible"
      overflowX="visible"
      backgroundColor="#fff"
      marginTop="0"
      borderRadius="1rem"
      closeButtonStyle={{ display: "none" }}
    >
      <div className="customize-subtopic-modal">
        <button
          type="button"
          className={`customize-subtopic-close ${isTopicMenuOpen ? "open" : ""}`}
          onClick={onClose}
        >
          ✕
        </button>
        <h3 className="customize-subtopic-title">Customize Subtopic</h3>

        <div className="customize-subtopic-section">
          <label className="customize-subtopic-label">Select Topic</label>
          <div className="customize-subtopic-select-wrap">
            <button
              type="button"
              className={`customize-subtopic-select ${isTopicMenuOpen ? "open" : ""}`}
              onClick={() => setIsTopicMenuOpen((prev) => !prev)}
            >
              <span className="customize-subtopic-select-value">
                {selectedTopic?.name || "Subtopic"}
              </span>
              <span className="customize-subtopic-select-level">
                {selectedTopic?.difficulty || ""}
              </span>
              <span className="customize-subtopic-select-arrow">
                {isTopicMenuOpen ? "\u2303" : "\u2304"}
              </span>
            </button>
            {isTopicMenuOpen ? (
              <div className="customize-subtopic-dropdown">
                {availableTopics.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`customize-subtopic-option ${
                      String(item.id) === String(selectedTopic?.id) ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedTopicId(item.id);
                      setDifficulty(normalizeDifficultyValue(item.difficulty || "Easy"));
                      setIsTopicMenuOpen(false);
                    }}
                  >
                    <span className="customize-subtopic-option-text">
                      <span className="customize-subtopic-option-name">{item.name}</span>
                      <span className="customize-subtopic-option-level">{item.difficulty}</span>
                    </span>
                    <span
                      className={`customize-subtopic-option-status ${getStatusClassName(
                        item.status
                      )}`}
                    >
                      {getStatusClassName(item.status) === "mastered" ? (
                        <img
                          src={images.topic_status_icon}
                          alt=""
                          className="customize-subtopic-option-status-icon"
                        />
                      ) : null}
                      {item.status}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="customize-subtopic-section">
          <label className="customize-subtopic-label">Difficulty Level</label>
          <div className="customize-subtopic-difficulty-row">
            {DIFFICULTY_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`customize-subtopic-difficulty-btn ${
                  difficulty === option ? "active" : ""
                }`}
                onClick={() => setDifficulty(option)}
              >
                {option.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="customize-subtopic-save-btn"
          onClick={handleSave}
          disabled={!selectedTopic}
        >
          Save Changes
        </button>
      </div>
    </Modal>
  );
};

export default CustomizeSubtopicModal;
